/**
 * Only for BellaBotPro page, page location pages/products/bellabotpro.page.tsx
 */

import { useState } from 'react'
import { useTranslations } from 'next-intl'
import { useChinaDetector } from '../common/region-locale'
import { ITopTabSwitch, Theme } from '../common/pd-meta-components-types'

type Section1Type = {
  backgroundImagePCUrl: string
  backgroundImageH5Url: string
  imageTitleUrl: string
  subTitle: string
  videoUrl: string
}

type Section2Type = {
  backgroundImagePCUrl: string
  title: string
  subTitle: string[]
  feature: {
    title: string
    style: string
  }[]
}

type Section3Type = {
  videoUrl: string
  title: string
  subTitle: string[]
}

type Section4Type = {
  title: string
  features: { iconUrl: string; value: string; title: string; desc: string }[]
  attributes: {
    value: string
    desc: string
  }[]
  robotImg: {
    url: string
    width: string
    length: string
    height: string
  }
}

type Section5Type = {
  title: string
  subTitle: string[]
  feature: {
    imgUrl: string
    title: string
    desc: string
  }[]
}

type Section6Type = {
  title: string
  subTitle: string[]
  feature: {
    imgUrl: string
    title: string
    desc: string
  }[]
}

type Section7Type = {
  title: string[]
  subTitle: string[]
  imgUrl: string
}

type Section8Type = {
  title: string
  subTitle: string[]
  features: { name?: string; desc?: string; mediaUrl: string }[]
}

type Section9Type = {
  title: string
  subTitle: string[]
  mediaUrl: string
}

type Section10Type = {
  feature: {
    type: string
    title?: string
    mediaUrl?: string
    subTitle?: string[]
    pcRank: number
    h5Rank: number
  }[]
}

type Section11Type = {
  title: string
  subTitle: string[]
  features: { name?: string; mediaUrl: string }[]
}

type Section12Type = {
  title: string
  subTitle: string[]
  features: { name?: string; mediaUrl: string; mediaUrlH5: string }[]
}
type Section13Type = {
  title: string
  subTitle: string[]
  features: { name: string; mediaUrl: string }[]
}

const T300_VARIANT_IMAGE_URLS = {
  standard: 'https://cdn.pudutech.com/official-website/pudut300/tab_PUDUT300Standard.png',
  lifting: 'https://cdn.pudutech.com/official-website/pudut300/tab_PUDUT300Lifting.png',
}

export function usePUDUT300() {
  const t = useTranslations()
  const inChina = useChinaDetector()
  const [section1Data] = useState<Section1Type>({
    backgroundImagePCUrl: 'https://cdn.pudutech.com/official-website/pudut300/t300-banner-pc.webp',
    backgroundImageH5Url: 'https://cdn.pudutech.com/official-website/pudut300/t300-banner-h5.webp',
    imageTitleUrl: 'https://cdn.pudutech.com/official-website/pudut300/product-name.svg',
    subTitle: t('pudut300.section1Data.subTitle'),
    videoUrl: inChina
      ? 'https://cdn.pudutech.com/official-website/pudut300/t300_cn.mp4'
      : 'https://cdn.pudutech.com/official-website/pudut300/t300_en.mp4',
  })

  const sectionVariantTabsData: ITopTabSwitch = {
    theme: Theme.Dark,
    mediaBorderRadius: 0,
    paddingTop: 'px120',
    paddingBottom: 'px120',
    mediaGroup: (['standard', 'lifting'] as const).map((key) => ({
      tabTitle: t(`pudut300.variantTabs.items.${key}.tabTitle`),
      title: t(`pudut300.variantTabs.items.${key}.title`),
      desc: t(`pudut300.variantTabs.items.${key}.desc`),
      mediaUrl: T300_VARIANT_IMAGE_URLS[key],
    })),
  }

  const [section2Data] = useState<Section2Type>({
    backgroundImagePCUrl: inChina
      ? 'https://cdn.pudutech.com/official-website/pudut300/section2-zh-pc.webp'
      : 'https://cdn.pudutech.com/official-website/pudut300/section2-pc.webp',
    title: t('pudut300.section2Data.title'),
    subTitle: [],
    feature: [
      {
        title: t('pudut300.section2Data.features.title1'),
        style: '',
      },
      {
        title: t('pudut300.section2Data.features.title2'),
        style: '',
      },
      {
        title: t('pudut300.section2Data.features.title3'),
        style: '',
      },
      {
        title: t('pudut300.section2Data.features.title4'),
        style: '',
      },
    ],
  })

  const [section3Data] = useState<Section3Type>({
    title: t('pudut300.section3Data.title'),
    subTitle: [t('pudut300.section3Data.subTitle1')],
    videoUrl: '',
  })

  const [section4Data] = useState<Section4Type>({
    title: t('pudut300.section4Data.title'),
    features: [
      {
        iconUrl: '1',
        value: t('pudut300.section4Data.features.value1'),
        title: t('pudut300.section4Data.features.title1'),
        desc: t('pudut300.section4Data.features.desc1'),
      },
      {
        iconUrl: '1',
        value: t('pudut300.section4Data.features.value2'),
        title: t('pudut300.section4Data.features.title2'),
        desc: t('pudut300.section4Data.features.desc2'),
      },
      {
        iconUrl: '1',
        value: t('pudut300.section4Data.features.value3'),
        title: t('pudut300.section4Data.features.title3'),
        desc: t('pudut300.section4Data.features.desc3'),
      },
      {
        iconUrl: '1',
        value: t('pudut300.section4Data.features.value4'),
        title: t('pudut300.section4Data.features.title4'),
        desc: t('pudut300.section4Data.features.desc4'),
      },
    ],
    attributes: [
      {
        value: t('pudut300.section4Data.attributes.value1'),
        desc: t('pudut300.section4Data.attributes.desc1'),
      },
      {
        value: t('pudut300.section4Data.attributes.value2'),
        desc: t('pudut300.section4Data.attributes.desc2'),
      },
      {
        value: t('pudut300.section4Data.attributes.value4'),
        desc: t('pudut300.section4Data.attributes.desc4'),
      },
      {
        value: t('pudut300.section4Data.attributes.value5'),
        desc: t('pudut300.section4Data.attributes.desc5'),
      },
    ],
    robotImg: {
      url: 'https://cdn.pudutech.com/official-website/pudut300/product-body-size.webp',
      width: t('pudut300.section4Data.robotImg.width'),
      length: t('pudut300.section4Data.robotImg.length'),
      height: t('pudut300.section4Data.robotImg.height'),
    },
  })

  const [section5Data] = useState<Section5Type>({
    title: t('pudut300.section5Data.title'),
    subTitle: [t('pudut300.section5Data.subTitle1')],
    feature: [
      {
        imgUrl: 'https://cdn.pudutech.com/official-website/pudut300/Auto-transport.webp',
        title: t('pudut300.section5Data.feature.title1'),
        desc: t('pudut300.section5Data.feature.desc1'),
      },
      {
        imgUrl: 'https://cdn.pudutech.com/official-website/pudut300/follow.webp',
        title: t('pudut300.section5Data.feature.title2'),
        desc: t('pudut300.section5Data.feature.desc2'),
      },
      {
        imgUrl: 'https://cdn.pudutech.com/official-website/pudut300/Power-assist.webp',
        title: t('pudut300.section5Data.feature.title3'),
        desc: t('pudut300.section5Data.feature.desc3'),
      },
    ],
  })

  const [section6Data] = useState<Section6Type>({
    title: t('pudut300.section6Data.title'),
    subTitle: [t('pudut300.section6Data.subTitle1')],
    feature: [
      {
        imgUrl: 'https://cdn.pudutech.com/official-website/pudut300/robot-function-1.webp',
        title: t('pudut300.section6Data.feature.title1'),
        desc: t('pudut300.section6Data.feature.desc1'),
      },
      {
        imgUrl: 'https://cdn.pudutech.com/official-website/pudut300/robot-function-2.webp',
        title: t('pudut300.section6Data.feature.title2'),
        desc: t('pudut300.section6Data.feature.desc2'),
      },
      {
        imgUrl: 'https://cdn.pudutech.com/official-website/pudut300/robot-function-3.webp',
        title: t('pudut300.section6Data.feature.title3'),
        desc: t('pudut300.section6Data.feature.desc3'),
      },
      {
        imgUrl: 'https://cdn.pudutech.com/official-website/pudut300/robot-function-4.webp',
        title: t('pudut300.section6Data.feature.title4'),
        desc: t('pudut300.section6Data.feature.desc4'),
      },
    ],
  })

  const [section7Data] = useState<Section7Type>({
    title: [t('pudut300.section7Data.title1'), t('pudut300.section7Data.title2')],
    subTitle: [t('pudut300.section7Data.subTitle1')],
    imgUrl: 'https://cdn.pudutech.com/official-website/pudut300/section7-bg.webp',
  })

  const [section8Data] = useState<Section8Type>({
    title: t('pudut300.section8Data.title'),
    subTitle: [t('pudut300.section8Data.subTitle1')],
    features: [
      {
        mediaUrl: '',
      },
      {
        name: t('pudut300.section8Data.features.name1'),
        desc: t('pudut300.section8Data.features.desc1'),
        mediaUrl: '',
      },
      {
        name: t('pudut300.section8Data.features.name2'),
        desc: t('pudut300.section8Data.features.desc2'),
        mediaUrl: '',
      },
      {
        name: t('pudut300.section8Data.features.name3'),
        desc: t('pudut300.section8Data.features.desc3'),
        mediaUrl: '',
      },
    ],
  })

  const [section9Data] = useState<Section9Type>({
    title: t('pudut300.section9Data.title'),
    subTitle: [t('pudut300.section9Data.subTitle1'), t('pudut300.section9Data.subTitle2')],
    mediaUrl: 'https://cdn.pudutech.com/official-website/pudut300/section9.webp',
  })

  const [section10Data] = useState<Section10Type>({
    feature: [
      {
        type: 'media',
        mediaUrl: 'https://cdn.pudutech.com/official-website/pudut300/section10-1.webp',
        pcRank: 1,
        h5Rank: 2,
      },
      {
        type: 'text',
        title: t('pudut300.section10Data.title1'),
        subTitle: [t('pudut300.section10Data.subTitle1')],
        pcRank: 2,
        h5Rank: 1,
      },
      {
        type: 'text',
        title: t('pudut300.section10Data.title2'),
        subTitle: [t('pudut300.section10Data.subTitle2')],
        pcRank: 3,
        h5Rank: 3,
      },
      {
        type: 'media',
        mediaUrl: 'https://cdn.pudutech.com/official-website/pudut300/section10-2.webp',
        pcRank: 4,
        h5Rank: 3,
      },
    ],
  })

  const [section11Data] = useState<Section11Type>({
    title: t('pudut300.section11Data.title'),
    subTitle: [t('pudut300.section11Data.subTitle1'), t('pudut300.section11Data.subTitle2')],
    features: [
      {
        name: t('pudut300.section11Data.features.name1'),
        mediaUrl: 'https://cdn.pudutech.com/official-website/pudut300/section11-1.webp',
      },
      {
        name: t('pudut300.section11Data.features.name2'),
        mediaUrl: 'https://cdn.pudutech.com/official-website/pudut300/section11-2.webp',
      },
    ],
  })

  const [section12Data] = useState<Section12Type>({
    title: t('pudut300.section12Data.title'),
    subTitle: [t('pudut300.section12Data.subTitle1')],
    features: [
      {
        name: t('pudut300.section12Data.features.name1'),
        mediaUrl: 'https://cdn.pudutech.com/official-website/pudut300/section12-1.webp',
        mediaUrlH5: 'https://cdn.pudutech.com/official-website/pudut300/section12-1-h5.webp',
      },
      {
        name: t('pudut300.section12Data.features.name2'),
        mediaUrl: 'https://cdn.pudutech.com/official-website/pudut300/section12-2.webp',
        mediaUrlH5: 'https://cdn.pudutech.com/official-website/pudut300/section12-2-h5.webp',
      },
      {
        name: t('pudut300.section12Data.features.name3'),
        mediaUrl: 'https://cdn.pudutech.com/official-website/pudut300/section12-3.webp',
        mediaUrlH5: 'https://cdn.pudutech.com/official-website/pudut300/section12-3-h5.webp',
      },
      {
        name: t('pudut300.section12Data.features.name4'),
        mediaUrl: 'https://cdn.pudutech.com/official-website/pudut300/section12-4.webp',
        mediaUrlH5: 'https://cdn.pudutech.com/official-website/pudut300/section12-4-h5.webp',
      },
    ],
  })
  const [section13Data] = useState<Section13Type>({
    title: t('pudut300.section13Data.title'),
    subTitle: [t('pudut300.section13Data.subTitle1')],
    features: [
      {
        name: t('pudut300.section13Data.features.name1'),
        mediaUrl: 'https://cdn.pudutech.com/official-website/pudut300/img_e-gates.webp',
      },
      {
        name: t('pudut300.section13Data.features.name2'),
        mediaUrl: 'https://cdn.pudutech.com/official-website/pudut300/img_elevator.webp',
      },
      {
        name: t('pudut300.section13Data.features.name3'),
        mediaUrl: 'https://cdn.pudutech.com/official-website/pudut300/img_pager.webp',
      },
      {
        name: t('pudut300.section13Data.features.name4'),
        mediaUrl: 'https://cdn.pudutech.com/official-website/pudut300/img_app.webp',
      },
    ],
  })
  return {
    section1Data,
    sectionVariantTabsData,
    section2Data,
    section3Data,
    section4Data,
    section5Data,
    section6Data,
    section7Data,
    section8Data,
    section9Data,
    section10Data,
    section11Data,
    section12Data,
    section13Data,
  }
}
